import React, { createContext, useContext } from 'react';
import { useWriterThread, type UseWriterThreadResult } from '@/hooks/useWriterThread';

const WriterThreadContext = createContext<UseWriterThreadResult | null>(null);

/**
 * Owns the Writer thread state above the layout branch.
 *
 * The chat pane is rendered from two different subtrees (compact vs
 * desktop shell), so it unmounts whenever the viewport crosses the
 * breakpoint. Keeping the thread — including a turn that is mid-stream —
 * in a provider that never moves means a resize, rotation or panel swap
 * can no longer drop the live turn.
 */
export const WriterThreadProvider: React.FC<{
  projectId: string | undefined;
  children: React.ReactNode;
}> = ({ projectId, children }) => {
  const value = useWriterThread(projectId);
  return (
    <WriterThreadContext.Provider value={value}>{children}</WriterThreadContext.Provider>
  );
};

/**
 * Uses the hoisted thread when a provider is present, and falls back to a
 * locally owned thread otherwise (tests, standalone embeds).
 */
export function useWriterThreadValue(projectId: string | undefined): UseWriterThreadResult {
  const ctx = useContext(WriterThreadContext);
  // Hook order stays stable; the fallback stays idle while a provider exists.
  const local = useWriterThread(ctx ? undefined : projectId);
  return ctx ?? local;
}
